"use client"
import { useState, useEffect, useContext, useRef } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Chip,
  alpha,
  Tooltip,
  Collapse,
  FormControlLabel,
  Checkbox,
  useTheme,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import CustomPagination from "./CustomPagination"
import ReplayIcon from "@mui/icons-material/Replay"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import ReceiptIcon from "@mui/icons-material/Receipt"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonIcon from "@mui/icons-material/Person"
import { ErpContext } from "../../context/ErpContext.js"
import "./TablaVentasComponent.css"
import TransaccionModal from "./TransaccionModal.js"
import { formatNumberForDisplay, getLocaleConfig } from "../../utils/numeros.js"

import dayjs from "dayjs"
import jsPDF from "jspdf"
import "jspdf-autotable"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"

import SpinnerLading from "../misc/SpinnerComponent.js"

const CajaComponent = () => {
  const theme = useTheme()
  
  const {
    user,
    getTransaccionesPagoFilter,
    getTipoMedioDePago,
    tipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    isNavbarCollapsed,
    getAndPostCajaDatos,
    getFechaHoraActualClient,
    getUbicacion,
    ubicaciones,
    getUsuario,
    usuarios,
    getTipoTransaccion,
    tipoTransaccion,
    tieneAccesoFuncionalidad, // 🔒 Función de verificación de permisos granulares
    formatoNumericoGlobal,
    getParametrosGlobales,
  } = useContext(ErpContext)

  const isFirstRender = useRef(true)

  const [startDate, setStartDate] = useState(dayjs())
  const [endDate, setEndDate] = useState(dayjs())
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const [transaccionesPagos, setTransaccionesPagos] = useState([])
  const [cajaData, setCajaData] = useState(null)

  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [selectedUsuario, setSelectedUsuario] = useState(null)
  const [selectedTipoTransaccion, setSelectedTipoTransaccion] = useState(null)
  const [cajaMontoFinal, setCajaMontoFinal] = useState(0)
  const [totalIngresos, setTotalIngresos] = useState(0)
  const [totalEgresos, setTotalEgresos] = useState(0)
  const [loading, setLoading] = useState(true)
  const [cajaMontoInicialCero, setCajaMontoInicialCero] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showPaymentFilters, setShowPaymentFilters] = useState(false)

  // Estados para las agrupaciones (Gerencia)
  const [agruparMediosPago, setAgruparMediosPago] = useState(false)
  const [agruparUbicacion, setAgruparUbicacion] = useState(false)
  const [mediosDePagoAgrupados, setMediosDePagoAgrupados] = useState([])
  const [ubicacionesAgrupadas, setUbicacionesAgrupadas] = useState([])
  const [combinadoAgrupado, setCombnadoAgrupado] = useState([])

  // 🆕 VERIFICAR PERMISOS DE COMPONENTES
  // IDs de funcionalidades definidos en: server/services/menuAccesoValidator.js (TRANSACCIONES10)
  const mostrarMontoFinal = tieneAccesoFuncionalidad(user, 'TRANSACCIONES10', 'montoFinalCaja')
  const mostrarMontoInicial = tieneAccesoFuncionalidad(user, 'TRANSACCIONES10', 'montoInicialCaja')
  const mostrarFiltrosMedioPago = tieneAccesoFuncionalidad(user, 'TRANSACCIONES10', 'filtrosMedioPago')
  const mostrarBotonAgregarTransaccion = tieneAccesoFuncionalidad(user, 'TRANSACCIONES10', 'btnAgregarTransaccion')
  const verTransaccionesIngresoCaja = tieneAccesoFuncionalidad(user, 'TRANSACCIONES10', 'verTransaccionesIngresoCaja')
  const verSoloMiUsuarioEnCaja = tieneAccesoFuncionalidad(user, 'TRANSACCIONES10', 'verSoloMiUsuarioEnCaja')
  const puedeAgruparCaja = tieneAccesoFuncionalidad(user, 'TRANSACCIONES10', 'agruparCaja')

  const increaseDate = () => {
    const baseDate = startDate || dayjs()
    const next = baseDate.add(1, "day")

    // si next es posterior a hoy, no avanzamos
    if (next.isAfter(dayjs(), "day")) return

    setStartDate(next)

    // Mantener rango válido si "desde" supera "hasta"
    if (endDate && next.isAfter(endDate, "day")) {
      setEndDate(next)
    }
  }

  const decreaseDate = () => {
    const previous = startDate ? startDate.subtract(1, "day") : dayjs()
    setStartDate(previous)
  }
  
  // Log de configuración de locale al montar el componente
  useEffect(() => {
    const localeConfig = getLocaleConfig(formatoNumericoGlobal || null)
    console.log("🔢 [CajaComponent] Configuración de locale detectada:", {
      locale: localeConfig.locale,
      numberingSystem: localeConfig.numberingSystem,
      thousandSeparator: localeConfig.thousandSeparator,
      decimalSeparator: localeConfig.decimalSeparator,
      ejemplo: formatNumberForDisplay(1234567.89, { locale: localeConfig.locale })
    })
  }, [formatoNumericoGlobal])
  
  useEffect(() => {
    // Aquí puedes llamar a la función
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    const today = dayjs()
    setStartDate(today)
    setEndDate(today)
    await getTipoMedioDePago("verEnCaja=true")
    await getUbicacion()
    await getUsuario()
    await getTipoTransaccion()
  }

  // 🔒 EFECTO: Precargar usuario actual si el permiso verSoloMiUsuarioEnCaja está activo
  useEffect(() => {
    if (verSoloMiUsuarioEnCaja && user && usuarios && usuarios.length > 0) {
      // Buscar el usuario actual en la lista de usuarios
      const usuarioActual = usuarios.find(u => u.id === user.id)
      if (usuarioActual && selectedUsuario?.id !== usuarioActual.id) {
        console.log(`🔒 [CajaComponent] Permiso verSoloMiUsuarioEnCaja activo: Precargando usuario ${usuarioActual.usuario}`)
        setSelectedUsuario(usuarioActual)
      }
    }
  }, [verSoloMiUsuarioEnCaja, user, usuarios])

  useEffect(() => {
    // Aquí puedes llamar a la función
    fetchData()
  }, [startDate])

  const fetchData = async () => {
    setLoading(true)
    const fechaDesde = startDate || dayjs()
    const fechaHasta = endDate && !endDate.isBefore(fechaDesde, "day") ? endDate : fechaDesde

    const { fechaServidor: fechaServidorDesde, zona } = getFechaHoraActualClient(fechaDesde)
    const { fechaServidor: fechaServidorHasta } = getFechaHoraActualClient(fechaHasta)

    // Leer el parámetro directamente para evitar race condition con el estado
    let montoInicialCero = cajaMontoInicialCero
    try {
      const params = await getParametrosGlobales(["cajaCalcularMontoCierreParaMontoInicial"])
      montoInicialCero = params?.cajaCalcularMontoCierreParaMontoInicial === "0"
      setCajaMontoInicialCero(montoInicialCero)
    } catch (_) {}

    // Pasar ubicación seleccionada o 'todas' si no hay filtro
    const ubicacionId = selectedUbicacion ? selectedUbicacion.id : 'todas'
    // console.log("ubicacionId: ", ubicacionId)
    
    const cajaPenUltimoDia = await getAndPostCajaDatos(
      dayjs(fechaServidorDesde).tz(zona).add(-1, "day"), 
      zona,
      ubicacionId
    )
    
    // Procesar respuesta: puede tener múltiples ubicaciones
    let montoCierreCalculado = 0
    
    if (cajaPenUltimoDia && cajaPenUltimoDia.ubicaciones) {
      // Sumar montos de todas las ubicaciones si no hay filtro específico
      if (!selectedUbicacion && cajaPenUltimoDia.ubicaciones.length > 1) {
        const montoTotalTodasUbicaciones = cajaPenUltimoDia.ubicaciones.reduce((sum, ub) => {
          return sum + (ub.ultimaCaja?.montoCierre || 0)
        }, 0)
        
        montoCierreCalculado = montoTotalTodasUbicaciones
        
        setCajaData({
          ubicaciones: cajaPenUltimoDia.ubicaciones,
          ultimaCaja: {
            montoCierre: montoTotalTodasUbicaciones,
            descripcion: 'Todas las ubicaciones'
          }
        })
      } else if (cajaPenUltimoDia.ubicaciones.length === 1) {
        // Una sola ubicación (filtrada o única)
        montoCierreCalculado = cajaPenUltimoDia.ubicaciones[0].ultimaCaja?.montoCierre || 0
        
        setCajaData({
          ubicaciones: cajaPenUltimoDia.ubicaciones,
          ultimaCaja: cajaPenUltimoDia.ubicaciones[0].ultimaCaja
        })
      } else {
        // Sin ubicaciones
        montoCierreCalculado = 0
        
        setCajaData({
          ubicaciones: [],
          ultimaCaja: { montoCierre: 0 }
        })
      }
    } else {
      // Formato antiguo o error
      montoCierreCalculado = cajaPenUltimoDia?.ultimaCaja?.montoCierre || 0
      setCajaData(cajaPenUltimoDia)
    }

    // console.log("fechaServidor: ", fechaServidor)

    const tipoMedioDePagoId = selectedTipoMedioDePago ? selectedTipoMedioDePago.id : null
    const medioDePagoId = selectedMedioDePago ? selectedMedioDePago.id : null
    const ubicacionFiltro = selectedUbicacion ? selectedUbicacion.id : null
    const usuarioId = selectedUsuario ? selectedUsuario.id : null
    const tipoTransaccionId = selectedTipoTransaccion ? selectedTipoTransaccion.id : null

    const data = await getTransaccionesPagoFilter(
      fechaServidorDesde,
      fechaServidorHasta,
      page,
      tipoMedioDePagoId,
      medioDePagoId,
      zona,
      ubicacionFiltro,
      usuarioId,
      tipoTransaccionId,
    )

    // console.log(data)

    // console.log("data: ", data)
    const currentPage = data.currentPage || 0
    setPage(currentPage)
    setTotalPages(data.totalPages)
    setTotalCount(data.total)

    // ✅ USAR TOTALES GLOBALES del backend (todas las páginas)
    const totalIngresosGlobal = data.totalesGlobales?.totalIngresos || 0
    const totalEgresosGlobal = data.totalesGlobales?.totalEgresos || 0
    const montoNetoGlobal = data.totalesGlobales?.montoNeto || 0

    const transaccionTransformed = data.results

    // 🆕 FILTRAR TRANSACCIONES: Solo mostrar si el tipo tiene verEnColumna definido
    // operacionCaja puede ser null (no afecta cálculos pero sí se muestra)
    const transaccionesConVerEnCaja = transaccionTransformed.filter(
      (transaccion) => {
        const tipo = transaccion.transaccion.tipoTransaccion;
        return (tipo.verEnColumna === 'Ingreso' || tipo.verEnColumna === 'Egreso');
      }
    )

    // 🔒 FILTRAR TRANSACCIONES SEGÚN PERMISO: Si no tiene permiso, ocultar ingresos
    const transaccionesFiltradas = verTransaccionesIngresoCaja
      ? transaccionesConVerEnCaja // Si tiene permiso, mostrar todas con verEnCaja = true
      : transaccionesConVerEnCaja.filter(
          (transaccion) => transaccion.transaccion.tipoTransaccion.operacionCaja !== "+"
        ) // Si NO tiene permiso, excluir ingresos

    // 💰 CÁLCULO DE TOTALES DE LA PÁGINA ACTUAL (para la fila "Totales" dentro de la tabla):
    // ⚠️ IMPORTANTE: Los cálculos financieros usan SIEMPRE la columna 'operacionCaja' ('+' o '-')
    // para determinar si afectan el balance.
    // La columna 'verEnColumna' ('Ingreso' o 'Egreso') solo se usa para VISUALIZACIÓN en la tabla,
    // permitiendo mostrar movimientos de dinero que no afectan el balance total (ej: cambio de ubicación).
    let totalIngresosPagina = 0
    let totalEgresosPagina = 0

    transaccionesFiltradas.forEach((transaccion) => {
      const monto = transaccion.pago.montoTotal
      if (transaccion.transaccion.tipoTransaccion.operacionCaja === "+") {
        totalIngresosPagina += Math.abs(monto)
      } else if (transaccion.transaccion.tipoTransaccion.operacionCaja === "-") {
        totalEgresosPagina += Math.abs(monto)
      }
    })

    // Usar el monto de cierre calculado anteriormente
    const montoCierre = montoCierreCalculado

    // ✅ Calcular monto final usando totales GLOBALES
    // Si montoInicialCero está activo, el monto inicial no se acumula en el monto final
    const montoFinalDato = (montoInicialCero ? 0 : montoCierre) + montoNetoGlobal
    
    // ✅ Guardar totales GLOBALES en estados (para el footer)
    setMontoTotalTransacciones(montoNetoGlobal)
    setCajaMontoFinal(montoFinalDato)
    setTotalIngresos(totalIngresosGlobal)
    setTotalEgresos(totalEgresosGlobal)

    // Guardar datos agrupados del backend
    setMediosDePagoAgrupados(data.mediosDePagoAgrupados || [])
    setUbicacionesAgrupadas(data.ubicacionesAgrupadas || [])
    setCombnadoAgrupado(data.combinadoAgrupado || [])

    // 🔒 Transformar solo las transacciones filtradas (visibles)
    const transaccionPagoTransformed = transaccionesFiltradas.map((transaccion) => {
      const pagos = [
        {
          descripcion: transaccion.pago.medioDePago.descripcion,
          monto: transaccion.pago.montoTotal,
        },
      ]

      const formattedDate = new Intl.DateTimeFormat("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(transaccion.transaccion.fecha))

      // 🆕 Fallback: Si verEnColumna no está configurado, usar operacionCaja para compatibilidad con transacciones antiguas
      const verEnColumna = transaccion.transaccion.tipoTransaccion.verEnColumna || 
        (transaccion.transaccion.tipoTransaccion.operacionCaja === "+" ? "Ingreso" : 
         transaccion.transaccion.tipoTransaccion.operacionCaja === "-" ? "Egreso" : null)

      return {
        id: `${transaccion.idTransaccion} - ${transaccion.idPago}`,
        createdAt: formattedDate,
        montoTotal: Math.abs(transaccion.pago.montoTotal), //Porque buscamos cada pago.
        pagos: pagos,
        tipoTransaccion: transaccion.transaccion.tipoTransaccion.descripcion,
        idTipoTransaccion: transaccion.transaccion.idTipoTransaccion,
        operacionCaja: transaccion.transaccion.tipoTransaccion.operacionCaja,
        verEnColumna: verEnColumna, // 🆕 Para visualización en columnas (con lógica de transferencia interna)
        ubicacion: transaccion.transaccion.ubicacion?.descripcion || "Sin ubicación",
        usuario: transaccion.transaccion.usuario?.usuario || "Sin usuario",
      }
    })

    // Agregar fila de totales al final (con totales de la página actual)
    const totalRow = {
      id: "TOTAL_ROW",
      createdAt: "",
      montoTotal: 0,
      pagos: [],
      tipoTransaccion: "TOTALES",
      idTipoTransaccion: null,
      operacionCaja: "TOTAL",
      ubicacion: "",
      usuario: "",
      totalIngresos: totalIngresosPagina, // ✅ Totales de la página actual
      totalEgresos: totalEgresosPagina,   // ✅ Totales de la página actual
      isTotal: true, // Flag para identificar la fila de totales
    }

    const transaccionesConTotales = [...transaccionPagoTransformed, totalRow]

    setTransaccionesPagos(transaccionesConTotales)
    setLoading(false)
  }

  // Función para manejar cuando se agrega una transacción - ÚNICA ADICIÓN
  const handleTransactionAdded = async (fecha, zona, ubicacionTransaccion) => {
    // console.log("handleTransactionAdded fecha: ", fecha, "zona: ", zona, "ubicacion:", ubicacionTransaccion)
    // Recalcular solo la ubicación de la transacción creada
    await getAndPostCajaDatos(fecha, zona, ubicacionTransaccion || 'todas', "update")
    await fetchData() // Refrescar los datos
  }

  // Función para generar PDF de la caja
  const generarPDFCaja = () => {
    // Validar que hay datos para generar el PDF
    if (!transaccionesPagos || transaccionesPagos.length === 0) {
      console.warn("No hay transacciones para generar el PDF")
      return
    }

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth() // 210mm para A4 (aproximadamente 210 puntos)
    const pageHeight = doc.internal.pageSize.getHeight()
    const MARGIN_X = 15 // Margen simétrico para centrar
    const contentWidth = pageWidth - (MARGIN_X * 2)
    let yPosition = 20

    // Colores
    const darkNavy = [26, 26, 26]
    const lightGray = [240, 240, 240]

    // ===========================================
    // ENCABEZADO
    // ===========================================
    doc.setFontSize(18)
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(...darkNavy)
    doc.text("Reporte de Caja", pageWidth / 2, yPosition, { align: "center" })
    yPosition += 10

    // Fecha del reporte
    doc.setFontSize(10)
    doc.setFont("Helvetica", "normal")
    doc.setTextColor(100, 100, 100)
    const fechaDesdeReporte = (startDate || dayjs()).format("DD/MM/YYYY")
    const fechaHastaReporte = (endDate && !endDate.isBefore(startDate || dayjs(), "day")
      ? endDate
      : startDate || dayjs()
    ).format("DD/MM/YYYY")
    const etiquetaFechaReporte = fechaDesdeReporte === fechaHastaReporte
      ? `Fecha: ${fechaDesdeReporte}`
      : `Período: ${fechaDesdeReporte} al ${fechaHastaReporte}`

    doc.text(etiquetaFechaReporte, pageWidth / 2, yPosition, { align: "center" })
    yPosition += 8

    // Filtros aplicados
    doc.setFontSize(9)
    doc.setFont("Helvetica", "italic")
    const filtros = []
    if (selectedUbicacion) filtros.push(`Ubicación: ${selectedUbicacion.descripcion}`)
    if (selectedUsuario) filtros.push(`Usuario: ${selectedUsuario.usuario}`)
    if (selectedTipoTransaccion) filtros.push(`Tipo Transacción: ${selectedTipoTransaccion.descripcion}`)
    if (selectedTipoMedioDePago) filtros.push(`Tipo Pago: ${selectedTipoMedioDePago.descripcion}`)
    if (selectedMedioDePago && selectedMedioDePago.id) filtros.push(`Medio Pago: ${selectedMedioDePago.descripcion}`)
    
    if (filtros.length > 0) {
      doc.text(`Filtros: ${filtros.join(" | ")}`, pageWidth / 2, yPosition, { align: "center", maxWidth: contentWidth })
      yPosition += 6
    } else {
      doc.text("Filtros: Todos", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 6
    }

    yPosition += 5

    // ===========================================
    // RESUMEN DE CAJA
    // ===========================================
    doc.setFontSize(12)
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(...darkNavy)
    doc.text("Resumen de Caja", pageWidth / 2, yPosition, { align: "center" })
    yPosition += 8

    // Calcular ancho del resumen (más estrecho y centrado)
    // Usar el mismo margen que la tabla para alineación
    const tableMarginForResumen = 15
    const resumenWidth = 100 // Ancho fijo más estrecho
    const resumenX = (pageWidth - resumenWidth) / 2 // Centrado perfecto
    const resumenHeight = mostrarMontoInicial && cajaData && cajaData.ultimaCaja ? 40 : 30

    doc.setFontSize(9)
    doc.setFont("Helvetica", "normal")
    doc.setTextColor(60, 60, 60)

    let resumenY = yPosition
    const resumenTextX = resumenX + 8 // Padding izquierdo

    // Monto Inicial (si tiene permiso)
    if (mostrarMontoInicial && cajaData && cajaData.ultimaCaja) {
      doc.setFont("Helvetica", "bold")
      doc.text("Monto Inicial:", resumenTextX, resumenY)
      doc.setFont("Helvetica", "normal")
      const montoInicial = cajaMontoInicialCero ? 0 : (cajaData.ultimaCaja.montoCierre || 0)
      doc.text(
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(montoInicial),
        resumenX + resumenWidth - 8,
        resumenY,
        { align: "right" }
      )
      resumenY += 7
    }

    // Total Ingresos
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(76, 175, 80) // Verde
    doc.text("Total Ingresos:", resumenTextX, resumenY)
    doc.setFont("Helvetica", "normal")
      doc.text(
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(totalIngresos),
        resumenX + resumenWidth - 8,
        resumenY,
        { align: "right" }
      )
    resumenY += 7

    // Total Egresos
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(244, 67, 54) // Rojo
    doc.text("Total Egresos:", resumenTextX, resumenY)
    doc.setFont("Helvetica", "normal")
      doc.text(
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(totalEgresos),
        resumenX + resumenWidth - 8,
        resumenY,
        { align: "right" }
      )
    resumenY += 7

    // Monto Final (si tiene permiso)
    if (mostrarMontoFinal) {
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.setFontSize(10)
      doc.text("Monto Final:", resumenTextX, resumenY)
      doc.text(
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(cajaMontoFinal),
        resumenX + resumenWidth - 8,
        resumenY,
        { align: "right" }
      )
    }

    yPosition = resumenY + 12

    // ===========================================
    // TABLA DE TRANSACCIONES
    // ===========================================
    doc.setFontSize(12)
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(...darkNavy)
    doc.text("Transacciones", pageWidth / 2, yPosition, { align: "center" })
    yPosition += 8

    // Preparar datos para la tabla (excluir fila de totales)
    const transaccionesParaTabla = Array.isArray(transaccionesPagos)
      ? transaccionesPagos.filter((t) => !t.isTotal)
      : []

    // ✅ Calcular totales de las transacciones de la página actual (para la fila de totales del PDF)
    let totalIngresosPaginaPDF = 0
    let totalEgresosPaginaPDF = 0
    
    transaccionesParaTabla.forEach((transaccion) => {
      if (transaccion.verEnColumna === "Ingreso") {
        totalIngresosPaginaPDF += transaccion.montoTotal
      } else if (transaccion.verEnColumna === "Egreso") {
        totalEgresosPaginaPDF += transaccion.montoTotal
      }
    })

    // Preparar datos para autoTable
    const tableData = transaccionesParaTabla.map((transaccion) => {
      // 🆕 Usar verEnColumna para determinar en qué columna mostrar (visualización)
      const ingreso = transaccion.verEnColumna === "Ingreso" 
        ? new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 2,
          }).format(transaccion.montoTotal)
        : "-"
      
      const egreso = transaccion.verEnColumna === "Egreso"
        ? new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 2,
          }).format(transaccion.montoTotal)
        : "-"

      const medioPago = transaccion.pagos && transaccion.pagos.length > 0
        ? transaccion.pagos[0].descripcion
        : "N/A"

      return [
        transaccion.id || "",
        transaccion.createdAt || "",
        ingreso,
        egreso,
        medioPago,
        transaccion.ubicacion || "",
        transaccion.usuario || "",
      ]
    })

    // Agregar fila de totales (con totales de la página actual)
    tableData.push([
      "TOTALES",
      "",
      new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(totalIngresosPaginaPDF), // ✅ Totales de la página actual
      new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(totalEgresosPaginaPDF), // ✅ Totales de la página actual
      "",
      "",
      "",
    ])

    // Generar tabla con autoTable
    // Usar márgenes simétricos para forzar el centrado automático
    const tableMarginX = 0 // Centrar una tabla de ~170mm de ancho
    
    doc.autoTable({
      startY: yPosition,
      head: [["ID", "Fecha", "Ingreso", "Egreso", "Medio de Pago", "Ubicación", "Usuario"]],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: darkNavy,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [60, 60, 60],
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      columnStyles: {
        0: { cellWidth: "auto", halign: "center", minCellWidth: 28 }, // ID - aumentado para evitar saltos de línea
        1: { cellWidth: "auto", halign: "center", minCellWidth: 38 }, // Fecha - aumentado para evitar saltos de línea
        2: { cellWidth: "auto", halign: "right", minCellWidth: 28 }, // Ingreso
        3: { cellWidth: "auto", halign: "right", minCellWidth: 28 }, // Egreso
        4: { cellWidth: "auto", halign: "center", minCellWidth: 30 }, // Medio de Pago
        5: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // Ubicación
        6: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // Usuario
      },
      styles: {
        cellPadding: 3,
        overflow: "linebreak",
        halign: "left",
      },
      didParseCell: function (data) {
        // Estilo para la fila de totales
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fillColor = [219, 234, 254] // Azul claro
          data.cell.styles.fontStyle = "bold"
          data.cell.styles.fontSize = 9
        }
      },
      margin: { left: tableMarginX, right: tableMarginX }, // Márgenes simétricos calculados para centrar
    })

    // Obtener la posición Y final después de la tabla
    const finalY = doc.lastAutoTable.finalY || yPosition + 50

    // ===========================================
    // PIE DE PÁGINA
    // ===========================================
    const pieY = pageHeight - 20
    doc.setFontSize(8)
    doc.setFont("Helvetica", "normal")
    doc.setTextColor(120, 120, 120)
    
    const fechaHoraGeneracion = new Date().toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    
    doc.text(
      `Generado el ${fechaHoraGeneracion} por ${user?.usuario || "Usuario"}`,
      pageWidth / 2,
      pieY,
      { align: "center" }
    )

    // Número de página
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.text(
        `Página ${i} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      )
    }

    // Descargar PDF
    const nombreArchivo = fechaDesdeReporte === fechaHastaReporte
      ? `Reporte_Caja_${fechaDesdeReporte.replace(/\//g, "-")}.pdf`
      : `Reporte_Caja_${fechaDesdeReporte.replace(/\//g, "-")}_a_${fechaHastaReporte.replace(/\//g, "-")}.pdf`
    doc.save(nombreArchivo)
  }

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      minWidth: 70,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => {
        // Fila de totales
        if (params.row.isTotal) {
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3", // Azul como medio de pago
                  fontSize: "1.05rem", // 3 puntos más grande
                }}
              >
                TOTALES
              </Typography>
            </Box>
          )
        }
        
        // Filas normales
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "createdAt",
      headerName: "Fecha",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: true,
      renderCell: (params) => {
        // Fila de totales - vacía
        if (params.row.isTotal) return null
        
        // Filas normales
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "ingreso",
      headerName: "Ingreso",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: true,
      valueGetter: (value, row) => {
        if (!row) return 0
        if (row.isTotal) return row.totalIngresos || 0
        // 🆕 Usar verEnColumna para determinar qué se muestra en esta columna
        if (row.verEnColumna !== "Ingreso") return 0
        return row.montoTotal || 0
      },
      renderCell: (params) => {
        const formatter = new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
        })

        // Fila de totales - calculada con operacionCaja
        if (params.row.isTotal) {
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3", // Azul como medio de pago
                  fontSize: "1.05rem", // 3 puntos más grande
                }}
              >
                {formatter.format(params.row.totalIngresos)}
              </Typography>
            </Box>
          )
        }

        // 🆕 Filas normales - Solo mostrar si verEnColumna === "Ingreso"
        if (params.row.verEnColumna !== "Ingreso") return null
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                bgcolor: alpha("#4CAF50", 0.1),
                color: "#4CAF50",
              }}
            >
              {formatter.format(params.row.montoTotal)}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "egreso",
      headerName: "Egreso",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: true,
      valueGetter: (value, row) => {
        if (!row) return 0
        if (row.isTotal) return row.totalEgresos || 0
        // 🆕 Usar verEnColumna para determinar qué se muestra en esta columna
        if (row.verEnColumna !== "Egreso") return 0
        return row.montoTotal || 0
      },
      renderCell: (params) => {
        const formatter = new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
        })

        // Fila de totales - calculada con operacionCaja
        if (params.row.isTotal) {
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3", // Azul como medio de pago
                  fontSize: "1.05rem", // 3 puntos más grande
                }}
              >
                {formatter.format(params.row.totalEgresos)}
              </Typography>
            </Box>
          )
        }

        // 🆕 Filas normales - Solo mostrar si verEnColumna === "Egreso"
        if (params.row.verEnColumna !== "Egreso") return null
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography variant="body2">{formatter.format(params.row.montoTotal)}</Typography>
          </Box>
        )
      },
    },
    {
      field: "tipoTransaccion",
      headerName: "Tipo de Transacción",
      flex: 1,
      minWidth: 180,
      headerAlign: "center",
      sortable: true,
      valueGetter: (value, row) => {
        if (!row) return "N/A"
        return row.tipoTransaccion || "N/A"
      },
      renderCell: (params) => {
        // Fila de totales - vacía
        if (params.row.isTotal) return null
        
        // Filas normales
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReceiptIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "pagos",
      headerName: "Medio de Pago",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      sortable: true,
      valueGetter: (value, row) => {
        if (!row) return "N/A"
        const pagos = Array.isArray(row.pagos) ? row.pagos : []
        if (pagos.length > 1) return "Múltiple"
        return pagos[0]?.descripcion || "N/A"
      },
      renderCell: (params) => {
        // Fila de totales - vacía
        if (params.row.isTotal) return null
        
        const pagos = Array.isArray(params.row.pagos) ? params.row.pagos : []

        if (pagos.length > 1) {
          return <Chip label="Múltiple" size="small" color="info" sx={{ fontWeight: "bold", display: "flex" }} />
        }

        return (
          <Chip
            icon={<PointOfSaleIcon fontSize="small" />}
            label={pagos[0]?.descripcion || "N/A"}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: "#00AAE4",
              borderColor: alpha("#00AAE4", 0.5),
              "& .MuiChip-icon": { color: "#00AAE4" },
              display: "flex",
            }}
          />
        )
      },
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => {
        // Fila de totales - vacía
        if (params.row.isTotal) return null
        
        // Filas normales
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "usuario",
      headerName: "Usuario",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => {
        // Fila de totales - vacía
        if (params.row.isTotal) return null
        
        // Filas normales
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
  ]

  // ─────────────────────────────────────────────────────────────
  // COLUMNAS Y DATOS PARA VISTAS AGRUPADAS
  // ─────────────────────────────────────────────────────────────

  const formatter = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" })

  const renderMontoIngreso = (params) => {
    if (params.row.isTotal) {
      return <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2196F3", fontSize: "1.05rem" }}>{formatter.format(params.value)}</Typography>
    }
    return <Typography variant="body2" sx={{ fontWeight: "bold", bgcolor: alpha("#4CAF50", 0.1), color: "#4CAF50" }}>{formatter.format(params.value)}</Typography>
  }

  const renderMontoEgreso = (params) => {
    if (params.row.isTotal) {
      return <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2196F3", fontSize: "1.05rem" }}>{formatter.format(params.value)}</Typography>
    }
    return <Typography variant="body2" sx={{ color: "#f44336" }}>{formatter.format(params.value)}</Typography>
  }

  const renderMontoNeto = (params) => {
    const val = params.value || 0
    return <Typography variant="body2" sx={{ fontWeight: "bold", color: val >= 0 ? "#4CAF50" : "#f44336" }}>{formatter.format(val)}</Typography>
  }

  // Modo 1 — Solo medios de pago
  const columnasMediosPago = [
    { field: "medioDePago", headerName: "Medio de Pago", flex: 1.5, minWidth: 180, headerAlign: "center",
      renderCell: (params) => {
        if (params.row.isTotal) return <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2196F3", fontSize: "1.05rem" }}>TOTALES</Typography>
        return <Chip icon={<PointOfSaleIcon fontSize="small" />} label={params.value} size="small" variant="outlined" sx={{ fontWeight: "bold", color: "#00AAE4", borderColor: alpha("#00AAE4", 0.5), "& .MuiChip-icon": { color: "#00AAE4" } }} />
      }
    },
    { field: "totalIngresos", headerName: "Ingreso Total", flex: 1, minWidth: 160, headerAlign: "center", renderCell: renderMontoIngreso },
    { field: "totalEgresos", headerName: "Egreso Total", flex: 1, minWidth: 160, headerAlign: "center", renderCell: renderMontoEgreso },
    { field: "montoNeto", headerName: "Monto Neto", flex: 1, minWidth: 160, headerAlign: "center", renderCell: renderMontoNeto },
  ]

  // Modo 2 — Solo ubicaciones
  const columnasUbicacion = [
    { field: "ubicacion", headerName: "Ubicación", flex: 1.5, minWidth: 180, headerAlign: "center",
      renderCell: (params) => {
        if (params.row.isTotal) return <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2196F3", fontSize: "1.05rem" }}>TOTALES</Typography>
        return <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} /><Typography variant="body2">{params.value}</Typography></Box>
      }
    },
    { field: "totalIngresos", headerName: "Ingreso Total", flex: 1, minWidth: 160, headerAlign: "center", renderCell: renderMontoIngreso },
    { field: "totalEgresos", headerName: "Egreso Total", flex: 1, minWidth: 160, headerAlign: "center", renderCell: renderMontoEgreso },
    { field: "montoNeto", headerName: "Monto Neto", flex: 1, minWidth: 160, headerAlign: "center", renderCell: renderMontoNeto },
  ]

  // Modo 3 — Combinado
  const columnasCombinado = [
    { field: "ubicacion", headerName: "Ubicación", flex: 1.2, minWidth: 160, headerAlign: "center",
      renderCell: (params) => {
        if (params.row.isTotal) return <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2196F3", fontSize: "1.05rem" }}>TOTALES</Typography>
        return <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} /><Typography variant="body2">{params.value}</Typography></Box>
      }
    },
    { field: "medioDePago", headerName: "Medio de Pago", flex: 1.2, minWidth: 160, headerAlign: "center",
      renderCell: (params) => {
        if (params.row.isTotal) return null
        return <Chip icon={<PointOfSaleIcon fontSize="small" />} label={params.value} size="small" variant="outlined" sx={{ fontWeight: "bold", color: "#00AAE4", borderColor: alpha("#00AAE4", 0.5), "& .MuiChip-icon": { color: "#00AAE4" } }} />
      }
    },
    { field: "totalIngresos", headerName: "Ingreso Total", flex: 1, minWidth: 150, headerAlign: "center", renderCell: renderMontoIngreso },
    { field: "totalEgresos", headerName: "Egreso Total", flex: 1, minWidth: 150, headerAlign: "center", renderCell: renderMontoEgreso },
    { field: "montoNeto", headerName: "Monto Neto", flex: 1, minWidth: 150, headerAlign: "center", renderCell: renderMontoNeto },
  ]

  // Función helper para armar datos con fila de totales
  const conFilaTotales = (items) => {
    const totalIngresos = items.reduce((s, i) => s + (i.totalIngresos || 0), 0)
    const totalEgresos = items.reduce((s, i) => s + (i.totalEgresos || 0), 0)
    return [
      ...items.map((item, idx) => ({ ...item, id: `agrupado_${idx}` })),
      { id: "TOTAL_ROW", ubicacion: "TOTALES", medioDePago: "TOTALES", totalIngresos, totalEgresos, montoNeto: totalIngresos - totalEgresos, isTotal: true }
    ]
  }

  // Determinar qué columnas y datos usar según los checkboxes activos
  const modoAgrupado = agruparMediosPago || agruparUbicacion
  let columnasActivas = columns
  let datosActivos = transaccionesPagos

  if (agruparMediosPago && !agruparUbicacion) {
    columnasActivas = columnasMediosPago
    datosActivos = conFilaTotales(mediosDePagoAgrupados)
  } else if (!agruparMediosPago && agruparUbicacion) {
    columnasActivas = columnasUbicacion
    datosActivos = conFilaTotales(ubicacionesAgrupadas)
  } else if (agruparMediosPago && agruparUbicacion) {
    columnasActivas = columnasCombinado
    datosActivos = conFilaTotales(combinadoAgrupado)
  }

  // Efecto para resetear medio de pago seleccionado
  useEffect(() => {
    if (selectedTipoMedioDePago == null) {
      setSelectedMedioDePago("")
    } else {
      getMedioDePagoIdTipoEspecifico(selectedTipoMedioDePago)
    }
  }, [selectedTipoMedioDePago])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    fetchData()
    setLoading(false)
  }, [page])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
    }).format(amount || 0)
  }

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", p: 2 }}>
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderRadius: 3,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          mb: 1, // Changed margin-bottom to 1 from 2
        }}
      >
        {/* Left section: Date picker */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", width: "100%", paddingRight: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={decreaseDate} size="small" sx={{ color: "#5b5b5f" }}>
                <ArrowBackIcon />
              </IconButton>
              <DatePicker
                label="Fecha Desde"
                format="DD/MM/YYYY"
                disableFuture
                maxDate={dayjs()}
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue)

                  if (newValue && endDate && newValue.isAfter(endDate, "day")) {
                    setEndDate(newValue)
                  }
                }}
                slotProps={{
                  textField: { 
                    size: "small", 
                    inputProps: { maxLength: 10 },
                    
                  },
                }}
                sx={{ width: 150 }}
              />
              <IconButton onClick={increaseDate} size="small" sx={{ color: "#5b5b5f" }}>
                <ArrowForwardIcon />
              </IconButton>
              <DatePicker
                label="Fecha Hasta"
                format="DD/MM/YYYY"
                disableFuture
                maxDate={dayjs()}
                value={endDate}
                onChange={(newValue) => {
                  if (newValue && startDate && newValue.isBefore(startDate, "day")) {
                    setEndDate(startDate)
                    return
                  }

                  setEndDate(newValue)
                }}
                slotProps={{
                  textField: { 
                    size: "small", 
                    inputProps: { maxLength: 10 },
                  },
                }}
                sx={{ width: 150 }}
              />
            </Box>
          </LocalizationProvider>

          
          {/* Ubicación and Usuario filters always visible in main menu */}
          <Autocomplete
            size="small"
            disablePortal
            options={ubicaciones}
            getOptionLabel={(option) => option.descripcion || ""}
            value={selectedUbicacion}
            sx={{ flex: 0.5, minWidth: 150 }}
            onChange={(event, newValue) => {
              setSelectedUbicacion(newValue)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ubicación"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            size="small"
            disablePortal
            disabled={verSoloMiUsuarioEnCaja}
            options={usuarios}
            getOptionLabel={(option) => option.usuario || ""}
            value={selectedUsuario}
            onChange={(event, newValue) => {
              setSelectedUsuario(newValue)
            }}
            sx={{ flex: 0.5, minWidth: 150 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuario"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            size="small"
            disablePortal
            options={
              Array.isArray(tipoTransaccion)
                ? tipoTransaccion.filter(
                    (t) => t.verEnColumna === "Ingreso" || t.verEnColumna === "Egreso"
                  )
                : []
            }
            getOptionLabel={(option) => option.descripcion || ""}
            value={selectedTipoTransaccion}
            onChange={(event, newValue) => {
              setSelectedTipoTransaccion(newValue)
            }}
            sx={{ flex: 0.5, minWidth: 150 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de transacción"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PointOfSaleIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", marginRight: 3 }}>
          <Tooltip title="Medios de pago">
            <IconButton
              onClick={() => setShowPaymentFilters(!showPaymentFilters)}
              size="medium"
              sx={{
                color: "#5b5b5f",
                border: `1px solid ${alpha("#5b5b5f", showPaymentFilters ? 0.5 : 0.3)}`,
                bgcolor: showPaymentFilters ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                  borderColor: "rgb(244, 177, 14)",
                },
              }}
            >
              <AttachMoneyIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Limpiar filtros">
            <IconButton
              onClick={() => {
                setSelectedTipoMedioDePago(null)
                setSelectedMedioDePago([])
                setSelectedUbicacion(null)
                setSelectedTipoTransaccion(null)
                // 🔒 Solo limpiar usuario si NO tiene el permiso verSoloMiUsuarioEnCaja activo
                if (!verSoloMiUsuarioEnCaja) {
                  setSelectedUsuario(null)
                }
                setStartDate(dayjs())
                setEndDate(dayjs())
              }}
              size="small"
              sx={{
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                borderRadius: 1.5,
                p: 0.75,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.15),
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <FilterAltOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* 🆕 PROTEGIDO POR PERMISOS: Solo usuarios con acceso pueden agregar transacciones */}
          {mostrarBotonAgregarTransaccion && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsModalOpen(true)}
              startIcon={<ReceiptIcon />}
              sx={{
                borderColor: "#5b5b5f",
                color: "#5b5b5f",
                padding: "8px 16px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "rgb(244, 177, 14)",
                  color: "rgb(244, 177, 14)",
                },
              }}
            >
              Agregar transacción
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
            onClick={fetchData}
            startIcon={<SearchIcon />}
            sx={{
              backgroundColor: "#5b5b5f",
              textTransform: "none",
              padding: "8px 16px",
              color: "white",
              "&:hover": {
                backgroundColor: alpha("rgb(244, 177, 14)", 0.8),
              },
            }}
          >
            Buscar
          </Button>

          <Tooltip title="Descargar PDF Caja">
            <span>
              <IconButton
                onClick={generarPDFCaja}
                disabled={loading || !transaccionesPagos || transaccionesPagos.length === 0}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {/* Resumen de Caja - 🆕 PROTEGIDO POR PERMISOS */}
        {mostrarMontoInicial && cajaData && cajaData.ultimaCaja && (
            <Paper
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: "#5b5b5f",
                color: "white",
                minWidth: 200,
                textAlign: "center",
                boxShadow: "0px 4px 12px rgba(244, 177, 14, 0.3)",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Monto Inicial:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {formatCurrency(cajaMontoInicialCero ? 0 : (cajaData?.ultimaCaja?.montoCierre || 0))}
              </Typography>
            </Paper>
          )}

      </Paper>

      {/* 🆕 PROTEGIDO POR PERMISOS: Solo usuarios con acceso ven filtros de medio de pago */}
      {mostrarFiltrosMedioPago && (
        <Collapse in={showPaymentFilters}>
          <Paper
            sx={{
              p: 2,
              mb: 1,
              borderRadius: 3,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              backgroundColor: alpha("rgb(244, 177, 14)", 0.05),
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 250, flexGrow: 1 }}
                disablePortal
                options={tipoMedioDePago}
                getOptionLabel={(option) => option.descripcion || ""}
                value={selectedTipoMedioDePago}
                onChange={(event, newValue) => {
                  setSelectedTipoMedioDePago(newValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de medio de pago"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon sx={{ color: "rgb(244, 177, 14)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                size="small"
                sx={{ minWidth: 250, flexGrow: 1 }}
                disablePortal
                options={medioDePagoIdTipoEspecifico}
                getOptionLabel={(option) => option.descripcion || ""}
                value={selectedMedioDePago || ""}
                onChange={(event, newValue) => {
                  setSelectedMedioDePago(newValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Medio de pago específico"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PointOfSaleIcon sx={{ color: "rgb(244, 177, 14)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* 🔒 GERENCIA: Checkboxes de agrupación */}
              {puedeAgruparCaja && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agruparMediosPago}
                        onChange={(e) => setAgruparMediosPago(e.target.checked)}
                        size="small"
                        sx={{ color: "rgb(244, 177, 14)", "&.Mui-checked": { color: "rgb(244, 177, 14)" } }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Agrupar medios de pago</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agruparUbicacion}
                        onChange={(e) => setAgruparUbicacion(e.target.checked)}
                        size="small"
                        sx={{ color: "rgb(244, 177, 14)", "&.Mui-checked": { color: "rgb(244, 177, 14)" } }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Agrupar por ubicación</Typography>}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </Collapse>
      )}

      {/* Contenido principal */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <SpinnerLading />
        </Box>
      ) : transaccionesPagos.length === 0 ? (
        /* Estado vacío - Sin datos */
        <Paper
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            p: 4,
            textAlign: "center",
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 80, color: alpha("#5b5b5f", 0.3), mb: 2 }} />
          <Typography variant="h5" sx={{ color: "#5b5b5f", mb: 1, fontWeight: "bold" }}>
            No se encontraron transacciones
          </Typography>
          <Typography variant="body1" sx={{ color: alpha("#5b5b5f", 0.7), mb: 3, maxWidth: 400 }}>
            No hay transacciones de pago para los filtros seleccionados. Intente ajustar las fechas o los filtros de
            medio de pago.
          </Typography>
          <Button
            variant="contained"
            onClick={fetchData}
            startIcon={<ReplayIcon />}
            sx={{
              backgroundColor: "#5b5b5f",
              color: "white",
              textTransform: "none",
              px: 3,
              "&:hover": {
                backgroundColor: alpha("rgb(244, 177, 14)", 0.8),
              },
            }}
          >
            Actualizar Búsqueda
          </Button>
        </Paper>
      ) : (
        /* Tabla con datos */
        <>
          <Paper
            sx={{
              flex: 1,
              borderRadius: 3,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <DataGrid
              key={`${isNavbarCollapsed ? "collapsed" : "expanded"}_${agruparMediosPago}_${agruparUbicacion}`}
              className="tablaVentasDataGrid"
              rows={datosActivos}
              columns={columnasActivas}
              getRowId={(row) => row.id}
              getRowClassName={(params) => (params.row.isTotal ? "total-row" : "")}
              pagination
              paginationMode={modoAgrupado ? "client" : "server"}
              getRowHeight={() => 35}
              headerHeight={35}
              sx={{
                width: "100%",
                border: 0,
                height: "100%",
                "& .MuiDataGrid-footerContainer": {
                  display: "none !important",
                },
                "& .total-row": {
                  backgroundColor: alpha("#2196F3", 0.08),
                  borderTop: `2px solid ${alpha("#2196F3", 0.3)}`,
                  "&:hover": {
                    backgroundColor: alpha("#2196F3", 0.12),
                  },
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  borderBottom: "2px solid #333",
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeader": {
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "white !important",
                  lineHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                  color: "white !important",
                },
                "& .MuiDataGrid-menuIconButton": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiInputBase-root": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiTablePagination-root": {
                  color: "black !important",
                },
                "& .MuiSelect-icon": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-row": {
                  "&:nth-of-type(even)": {
                    backgroundColor: "#F7F7F7",
                  },
                  "&:hover": {
                    backgroundColor: "#E3EAFD !important",
                    transition: "background 0.3s ease",
                  },
                },
                "& .row-devolucion": {
                  backgroundColor: alpha("#9e9e9e !important", 0.05),
                  "&:hover": {
                    backgroundColor: alpha("#9e9e9e !important", 0.1),
                  },
                },
                "& .MuiDataGrid-virtualScroller": {
                  lineHeight: "none !important",
                  height: "10px !imporant",
                },
                "& .MuiDataGrid-footerContainer": {
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "#ffffff",
                  borderTop: "1px solid #ddd",
                  zIndex: 10,
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                  width: "2px",
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "10px",
                },
              }}
              components={{
                Pagination: () => null,
              }}
            />
          </Paper>

          {/* Footer con paginación - 🆕 Pasar totales globales para el footer */}
          <Box sx={{ flexShrink: 0, pt: 2 }}>
            <CustomPagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              montoTotalTransacciones={montoTotalTransacciones}
              vieneDe={"tablaCaja"}
              montoFinalCaja={cajaMontoFinal}
              mostrarMontoFinal={mostrarMontoFinal}
              totalIngresos={totalIngresos}
              totalEgresos={totalEgresos}
              onPageChange={(newPage) => setPage(newPage)}
            />
          </Box>
        </>
      )}

      {/* MODAL AGREGADO - ÚNICA ADICIÓN */}
      <TransaccionModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fechaTransaccion={startDate}
        onTransactionAdded={handleTransactionAdded}
      />
    </Box>
  )
}

export default CajaComponent
